import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { HeartHandshake, Quote, ArrowRight } from "lucide-react";
import { MaskedLine, FadeUp, Stagger, StaggerItem, SmartImage, Magnetic } from "@/components/motion-primitives";
import { useConsult } from "@/components/ConsultProvider";
import { TEAM, VALUES, HERO_IMG } from "@/data/content";

const AboutHero = () => (
  <section className="relative overflow-hidden bg-navy pb-28 pt-40 text-white" data-testid="about-hero">
    <div className="absolute inset-0">
      <SmartImage src={HERO_IMG} alt="University campus" className="h-full w-full opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/85 to-navy" />
    </div>
    <div className="noise-overlay absolute inset-0" aria-hidden="true" />
    <div className="relative mx-auto max-w-7xl px-6">
      <MaskedLine delay={0.9}>
        <span className="font-accent text-xs font-semibold uppercase tracking-[0.3em] text-gold">About Us · Mehdipatnam, Hyderabad</span>
      </MaskedLine>
      <h1 className="mt-6 max-w-4xl font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl">
        <MaskedLine delay={1.0}>The people behind your</MaskedLine>
        <MaskedLine delay={1.12}><span className="italic text-gold">global future.</span></MaskedLine>
      </h1>
    </div>
  </section>
);

const Mission = () => (
  <section className="mx-auto max-w-7xl px-6 py-24 md:py-32" data-testid="mission-section">
    <div className="grid gap-16 lg:grid-cols-12">
      <FadeUp className="lg:col-span-5">
        <p className="font-accent text-xs font-bold uppercase tracking-[0.3em] text-gold">Our Mission</p>
        <h2 className="mt-4 font-heading text-3xl font-extrabold leading-tight tracking-tight text-navy sm:text-5xl">
          Every student deserves a <span className="italic text-gold">fair shot</span> at the world.
        </h2>
        <div className="mt-8 flex items-center gap-3 text-navy">
          <HeartHandshake className="h-6 w-6 text-gold" />
          <span className="font-accent text-sm font-semibold uppercase tracking-widest">Your Trusted Partner in Global Education</span>
        </div>
      </FadeUp>
      <FadeUp delay={0.15} className="lg:col-span-7">
        <div className="space-y-6 text-lg leading-relaxed text-muted">
          <p>
            Student First Overseas Education Consultants exists to help students gain admission to accredited universities abroad — with honest visa processing guidance and long-term career planning at the core of everything we do.
          </p>
          <p>
            Over the years we have gained deep expertise in overseas education and become a trusted resource for admissions to Australia, the UK and the USA — alongside Canada, Germany, France, New Zealand, Ireland and Singapore.
          </p>
          <p>
            What makes us different is simple: zero prepayment, transparent pricing, and counselors who follow up at every single step — from your first profile evaluation to the day your flight takes off.
          </p>
        </div>
      </FadeUp>
    </div>
  </section>
);

const ValuesMarquee = () => (
  <div className="border-y border-line bg-white py-5" aria-hidden="true">
    <Marquee speed={30} gradient={false} pauseOnHover>
      {VALUES.map((v) => (
        <span key={v.title} className="mx-8 flex items-center gap-8 font-heading text-lg font-bold uppercase tracking-[0.3em] text-navy/25">
          {v.title} <span className="text-gold">✦</span>
        </span>
      ))}
    </Marquee>
  </div>
);

const Values = () => (
  <section className="mx-auto max-w-7xl px-6 py-24 md:py-32" data-testid="values-section">
    <FadeUp>
      <p className="font-accent text-xs font-bold uppercase tracking-[0.3em] text-gold">What We Stand For</p>
      <h2 className="mt-4 max-w-2xl font-heading text-3xl font-extrabold tracking-tight text-navy sm:text-5xl">
        Six commitments, <span className="italic text-gold">kept daily</span>
      </h2>
    </FadeUp>
    <Stagger className="mt-16 grid grid-cols-1 gap-x-12 gap-y-14 md:grid-cols-2 lg:grid-cols-3" gap={0.08}>
      {VALUES.map((v, i) => (
        <StaggerItem key={v.title}>
          <div data-testid={`value-card-${i}`} className="group border-t-2 border-line pt-8 transition-colors duration-300 hover:border-gold">
            <span className="font-heading text-5xl font-extrabold text-navy/10 transition-colors duration-300 group-hover:text-gold/40">
              {String(i + 1).padStart(2, "0")}
            </span>
            <h3 className="mt-4 font-heading text-xl font-bold text-navy">{v.title}</h3>
            <p className="mt-3 text-base leading-relaxed text-muted">{v.desc}</p>
          </div>
        </StaggerItem>
      ))}
    </Stagger>
  </section>
);

const Team = () => (
  <section className="border-y border-line bg-white py-24 md:py-32" data-testid="team-section">
    <div className="mx-auto max-w-7xl px-6">
      <FadeUp className="text-center">
        <p className="font-accent text-xs font-bold uppercase tracking-[0.3em] text-gold">Leadership</p>
        <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-navy sm:text-5xl">
          Meet the <span className="italic text-gold">founder</span>
        </h2>
      </FadeUp>
      <Stagger className="mx-auto mt-16 max-w-md">
        {TEAM.map((m, i) => (
          <StaggerItem key={m.name}>
            <article data-testid={`team-card-${i}`} className="group text-center">
              <SmartImage
                src={m.img}
                alt={`${m.name}, ${m.role}`}
                className="aspect-[4/5] w-full rounded-xl border border-line shadow-soft transition-transform duration-500 group-hover:-translate-y-1.5"
              />
              <div className="mt-6 inline-block border-l-2 border-gold pl-5 text-left">
                <h3 className="font-heading text-2xl font-bold text-navy">{m.name}</h3>
                <p className="font-accent text-xs font-semibold uppercase tracking-widest text-gold">{m.role}</p>
                <p className="mt-2 text-base text-muted">{m.line}</p>
              </div>
            </article>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const FOUNDER_HIGHLIGHTS = [
  { title: "Personal Guidance", desc: "One-to-one support focused on each student's individual goals." },
  { title: "15+ Years of Experience", desc: "Extensive experience helping students pursue higher education abroad." },
  { title: "From First Call to Visa Day", desc: "A personal approach that supports students throughout their overseas education journey." },
];

const FounderSection = () => {
  const { openConsult } = useConsult();
  return (
    <section className="mx-auto max-w-7xl px-6 py-24 md:py-32" data-testid="founder-section" aria-label="Founder introduction">
      <div className="grid grid-cols-1 gap-x-16 gap-y-12 lg:grid-cols-2">
        <FadeUp className="order-2 lg:order-none lg:col-start-1 lg:row-span-5">
          <div className="relative mx-auto max-w-md lg:sticky lg:top-32">
            <div className="rounded-2xl border border-line bg-white p-3 shadow-soft">
              <SmartImage
                src="/afshan.webp"
                alt="Afshan, Founder & Head Counselor of Student First Overseas"
                className="aspect-[4/5] w-full rounded-xl"
                data-testid="founder-portrait"
              />
            </div>
            <span
              data-testid="founder-badge"
              className="absolute -bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gold px-6 py-2.5 font-accent text-xs font-bold uppercase tracking-[0.2em] text-white shadow-lift"
            >
              15+ Years of Experience
            </span>
          </div>
        </FadeUp>

        <FadeUp className="order-1 lg:col-start-2">
          <p className="font-accent text-xs font-bold uppercase tracking-[0.3em] text-gold">Meet the Founder</p>
          <h2 className="mt-4 font-heading text-3xl font-extrabold leading-tight tracking-tight text-navy sm:text-4xl lg:text-5xl">
            <span className="text-gold">15 Years</span> of Guiding Students Toward Their Global Future
          </h2>
          <p className="mt-4 font-accent text-sm font-semibold uppercase tracking-widest text-muted">
            Afshan · Founder & Head Counselor
          </p>
          <p className="mt-6 text-lg leading-relaxed text-muted">
            With 15 years of experience in overseas education, Afshan has dedicated her career to helping students pursue higher education abroad. Her approach is personal, practical, and student-focused — guiding students through every important stage of their journey, from understanding their study options to preparing for their overseas education journey.
          </p>
          <p className="mt-5 text-lg leading-relaxed text-muted">
            Having worked closely with students and families over the years, she believes that choosing to study abroad is more than an application process. It is a major life decision that requires the right guidance, clarity, and confidence. Her commitment is to make that journey simpler, more transparent, and more reassuring for every student.
          </p>
        </FadeUp>

        <FadeUp delay={0.1} className="order-3 lg:col-start-2">
          <div className="space-y-7 border-l-2 border-gold pl-7">
            {FOUNDER_HIGHLIGHTS.map((h, i) => (
              <div key={h.title} data-testid={`founder-highlight-${i}`} className="flex gap-5">
                <span className="font-heading text-xl font-extrabold text-gold">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className="font-heading text-base font-bold uppercase tracking-wide text-navy">{h.title}</h3>
                  <p className="mt-1.5 text-base leading-relaxed text-muted">{h.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </FadeUp>

        <FadeUp delay={0.15} className="order-4 lg:col-start-2">
          <figure className="rounded-xl border border-line bg-white p-8 shadow-soft" data-testid="founder-quote">
            <Quote className="h-8 w-8 text-gold/40" strokeWidth={1.5} />
            <blockquote className="mt-4 text-xl italic leading-relaxed text-navy">
              "Every student has a different ambition. My role is to understand that ambition, guide the journey, and help turn it into a clear path toward higher education abroad."
            </blockquote>
            <figcaption className="mt-5 font-accent text-xs font-semibold uppercase tracking-widest text-muted">
              — Afshan, Founder & Head Counselor
            </figcaption>
          </figure>
        </FadeUp>

        <FadeUp delay={0.2} className="order-5 lg:col-start-2">
          <p className="font-heading text-xl font-bold text-navy">Start Your Journey With Guidance You Can Trust</p>
          <Magnetic className="mt-5">
            <button
              onClick={() => openConsult()}
              data-testid="founder-cta-button"
              className="group inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 font-accent text-sm font-bold uppercase tracking-widest text-white shadow-soft transition-colors duration-300 hover:bg-gold-soft"
            >
              Talk to Afshan
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Magnetic>
        </FadeUp>
      </div>
    </section>
  );
};

export default function About() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="about-page"
    >
      <AboutHero />
      <Mission />
      <ValuesMarquee />
      <Values />
      <Team />
      <FounderSection />
    </motion.div>
  );
}
